import {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

// material ui
import { Divider, Grid, Stack, Typography } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";

// material ui icons
import DeleteRounded from "@mui/icons-material/DeleteRounded";

// project import
import Selector from "components/select/selector";
import SmallIconButton from "components/buttons/smallIconButton";
import { useTranslation } from "utils/locales/utilityFunctions";
import AutoCompleteFiltered, { OptionType } from "components/input/autoCompleteFiltered";

const MIX_OPTIONS = Array.from({ length: 10 }, (_, i) => ({
  label: `${(i + 1) * 10}%`,
  id: `${(i + 1) * 10}`,
}));

export type RawWoodSpeciesItem = {
  uid: number;
  id: string;
  mix: string;
};

export type RawWoodInputInformationSpeciesHandle = {
  getSpeciesArray: () => RawWoodSpeciesItem[];
};

type RawWoodInputInformationSpeciesProps = {
  initSpeciesArray?: RawWoodSpeciesItem[];
  speciesOptions: any[];
  disableInputs: boolean;
};

const RawWoodInputInformationSpecies: ForwardRefRenderFunction<
  RawWoodInputInformationSpeciesHandle,
  RawWoodInputInformationSpeciesProps
> = ({ initSpeciesArray, speciesOptions, disableInputs }, ref) => {
  // --- Hooks --- //
  const { translate } = useTranslation();

  // --- States --- //
  const [speciesArray, setSpeciesArray] = useState([{ uid: 0, id: "non", mix: "" }]);

  // --- Effects --- //
  useEffect(() => {
    if (
      initSpeciesArray 
      // &&
      // initSpeciesArray.length > 0 
      // &&
      // speciesArray.length === 1 &&
      // speciesArray[0].id === "non"
    ) {
      if (initSpeciesArray.length === 0) {
        setSpeciesArray([{ uid: 1, id: "non", mix: "" }]);
        return;
      }
      // Initialize speciesArray with initSpeciesArray if it is not already set
      setSpeciesArray(initSpeciesArray);
    }
  }, [initSpeciesArray]);

  // --- Handlers --- //
  // 👇 Expose getSpeciesArray to the parent
  useImperativeHandle(ref, () => ({
    getSpeciesArray: () => speciesArray,
  }));

  const updateHandler = (uid: number, field: "id" | "mix", e: SelectChangeEvent<string> | OptionType | null) => {
    let value: string = "";
    if (field === "id") {
      const option = e as OptionType | null;
      value = option ? option.id : "non";
      // value =(e as SelectChangeEvent<string>).target.value;
    } else if (field === "mix") {
      value = (e as SelectChangeEvent<string>).target.value;
    }
    // Update the field for the target row
    let updated = speciesArray.map((item) =>
      item.uid === uid ? { ...item, [field]: value } : item
    );
    // Find the current item
    const currentItem = updated.find((item) => item.uid === uid);
    // If the user just selected a species on a previously empty row, set its mix to remaining
    if (field === "id" && currentItem?.id !== "non" && currentItem?.mix === "") {
      const totalMixBefore = updated.reduce((sum, item) => {
        const val = parseInt(item.mix, 10);
        return sum + (isNaN(val) ? 0 : val);
      }, 0);
      const remainingMix = Math.max(0, 100 - totalMixBefore);

      // Only set the mix if remaining > 0
      if (remainingMix > 0) {
        updated = updated.map((item) =>
          item.uid === uid ? { ...item, mix: remainingMix.toString() } : item
        );
      }
    }
    // Recalculate totalMix after possible changes
    const totalMix = updated.reduce((sum, item) => {
      const val = parseInt(item.mix, 10);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
    const remainingMix = Math.max(0, 100 - totalMix);
    // Only add a new empty row if total < 100 AND no row has empty mix
    const hasEmptyMix = updated.some((item) => item.mix === "");
    if (remainingMix > 0 && !hasEmptyMix) {
      const maxUid = Math.max(...updated.map((item) => item.uid));
      updated.push({
        uid: maxUid + 1,
        id: "non",
        mix: "",
      });
    }
    setSpeciesArray(updated);
  };

  const removeHandler = (uid: number) => {
    let updated = speciesArray.filter((item) => item.uid !== uid);
    // Always keep at least one row
    if (updated.length === 0) {
      updated = [{ uid: 1, id: "non", mix: "" }];
    }
    // Recalculate total mix
    const totalMix = updated.reduce((sum, item) => {
      const val = (!item.id || item.id === "non") ? 0 : parseInt(item.mix, 10);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
    const remainingMix = Math.max(0, 100 - totalMix);
    // Only add new empty row if no row has empty mix
    const hasEmptyMix = updated.some((item) => item.mix === "");
    if (remainingMix > 0 && !hasEmptyMix) {
      const maxUid = Math.max(...updated.map((item) => item.uid));
      updated.push({
        uid: maxUid + 1,
        id: "non",
        mix: "",
      });
    }
    setSpeciesArray(updated);
  };

  const isTotalOver100 = () => {
    const total = speciesArray.reduce((sum, item) => {
      const val = parseInt(item.mix, 10);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
    return total > 100;
  };

  return (
    <>
      {speciesArray.map((item) => (
        <Grid item xs={12} sm={12 / 2} key={item.uid}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={8}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <SmallIconButton
                  tooltipText={translate(
                    "input-dialog-raw-wood-tab-information-remove-species-tooltip"
                  )}
                  status="enabled"
                  onClick={() => removeHandler(item.uid)}
                  orientation="horizontal"
                  disabled={!item.id || item.id === "non" || disableInputs}
                >
                  <DeleteRounded fontSize="small" color={"primary"} />
                </SmallIconButton>

                <Divider orientation="vertical" flexItem />

                <AutoCompleteFiltered
                  options={speciesOptions}
                  value={speciesOptions.find((opt) => opt.id === item.id && item.id !== "non") || null}
                  changeHandler={(_, v) => updateHandler(item.uid, "id", v)}
                  disabled={disableInputs}
                  numberOfOptions={5}
                  label={translate("input-dialog-raw-wood-tab-information-species")}
                  placeholder={translate("species-search")}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Selector
                title={translate("input-dialog-raw-wood-tab-information-species-procentage")}
                value={item.mix}
                options={MIX_OPTIONS}
                label={true}
                fullWidth={true}
                disabled={!item.id || item.id === "non" || disableInputs}
                error={isTotalOver100()}
                changeHandler={(e) => updateHandler(item.uid, "mix", e)}
              />

              {isTotalOver100() && (
                <Typography variant="caption" color="error">
                  {translate("input-dialog-raw-wood-tab-information-species-over-100")}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      ))}
    </>
  );
};

export default forwardRef(RawWoodInputInformationSpecies);
